import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { isChunkLoadError } from 'utils/chunkLoadError';

const CHUNK_IMPORT_RETRIES = 2;
const CHUNK_RETRY_DELAY_MS = 400;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function importWithChunkRetry<T>(
  factory: () => Promise<T>,
  attempt = 0,
): Promise<T> {
  try {
    return await factory();
  } catch (error) {
    if (isChunkLoadError(error) && attempt < CHUNK_IMPORT_RETRIES) {
      await delay(CHUNK_RETRY_DELAY_MS * (attempt + 1));
      return importWithChunkRetry(factory, attempt + 1);
    }
    throw error;
  }
}

/** Warm the module cache before navigation (e.g. link hover). Failures are ignored. */
export function preloadRoute(factory: () => Promise<unknown>): void {
  void importWithChunkRetry(factory).catch(() => {
    // Prefetch is best-effort; navigation will retry or surface an error boundary.
  });
}

// Pages use mixed prop types (`{}`, no props, etc.); avoid `unknown` props constraint.
export function lazyRoute<T extends ComponentType>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => importWithChunkRetry(factory));
}

export function lazyNamedRoute<T extends ComponentType>(
  factory: () => Promise<Record<string, unknown>>,
  exportName: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const module = await importWithChunkRetry(factory);
    const component = module[exportName];
    if (typeof component !== 'function') {
      throw new Error(
        `lazyNamedRoute: "${exportName}" is not exported from the module`,
      );
    }
    return { default: component as T };
  });
}
